import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ReducerState } from '@/services/common/lib/reducerState';
import { ConnectedWallet, usePrivy, useWallets } from '@privy-io/react-auth';
import { findOrCreateBiconomyAccountFromEOA } from '../services/accountAbstractionService';
import {
  setInitialized,
  setLoading,
  setSmartAccount,
  setSmartAccountAddress,
} from '../store/accountAbstractionActions';
import { useAccountAbstractionDispatch, useAccountAbstractionStore } from '../store/AccountAbstractionContext';
import { updateRegisteredUser } from '@/services/user/user.service';
import config from '@/common/config';

export const useSmartAccountInitialization = () => {
  const { wallets } = useWallets();
  const { ready, authenticated } = usePrivy();
  const dispatch = useAccountAbstractionDispatch();
  const accountAbstractionStore = useAccountAbstractionStore();
  const isLoading = useSelector((state: ReducerState) => !!state.auth.loading);
  const userId = useSelector((state: ReducerState) => state.auth.id);
  const smartWalletAddress = useSelector((state: ReducerState) => state.auth.smartWalletAddress);

  const initSmartAccount = useCallback(
    async (embeddedWallet: ConnectedWallet, smartWalletAddress?: `0x${string}` | undefined): Promise<void> => {
      dispatch(setLoading(true));
      try {
        await embeddedWallet.switchChain(config.chain?.id);
        const smartAccount = await findOrCreateBiconomyAccountFromEOA({
          wallet: embeddedWallet,
          smartWalletAddress,
        });
        const address = await smartAccount.getAddress();

        if (!smartWalletAddress) {
          // save in BE
          void updateRegisteredUser({ smartWalletAddress: address });
        }
        dispatch(setSmartAccount(smartAccount));
        dispatch(setSmartAccountAddress(address));
        dispatch(setInitialized(true));
      } catch (error) {
        console.error('Error initializing smart account', error);
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (!ready || !authenticated || accountAbstractionStore.state.initialized || !config.smartWalletEnabled) return;

    const embeddedWallet = wallets.find((wallet) => wallet.walletClientType === 'privy');
    if (
      embeddedWallet &&
      !accountAbstractionStore.state.smartAccount &&
      !accountAbstractionStore.state.loading &&
      !isLoading &&
      userId
    ) {
      void initSmartAccount(embeddedWallet, smartWalletAddress);
    }
  }, [
    wallets,
    ready,
    authenticated,
    smartWalletAddress,
    accountAbstractionStore.state.initialized,
    accountAbstractionStore.state.smartAccount,
    accountAbstractionStore.state.smartWalletAddress,
    accountAbstractionStore.state.loading,
    isLoading,
    userId,
    initSmartAccount,
  ]);
};
